import { useState, useEffect } from 'react';
import { LogIn, LogOut, Download, X, Share, Plus, Droplets, Warehouse } from 'lucide-react';
import OperatoreIn from './OperatoreIn';
import OperatoreOut from './OperatoreOut';
import OperatoreStatoDocce from './OperatoreStatoDocce';
import OperatoreCellaIn from './OperatoreCellaIn';
import OperatoreCellaOut from './OperatoreCellaOut';
import { Toaster } from '@/components/ui/sonner';

const OperatorePortal = () => {
  const [activeTab, setActiveTab] = useState('in');
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Imposta titolo della pagina
    document.title = 'Operatore - Blu Marlin';
    
    // Controlla se l'app è già installata
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    // Ascolta evento beforeinstallprompt per PWA (Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallAndroid = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1628] to-[#1A2D4A]">
      {/* Header con logo */}
      <header className="bg-[#FFE500] py-4 px-4 shadow-lg">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/icons/logo-blumarlin.png" 
              alt="Blu Marlin Bivalvi" 
              className="w-14 h-14 object-contain rounded-lg shadow"
            />
            <div>
              <h1 className="text-lg font-bold text-[#0A1628] tracking-tight leading-tight">BLU MARLIN</h1>
              <p className="text-xs text-[#0A1628]/70 font-medium">BIVALVI S.R.L.</p>
            </div>
          </div>
          {!isInstalled && (
            <button
              onClick={() => setShowInstallGuide(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[#0A1628] text-[#FFE500] rounded-lg text-xs font-bold uppercase shadow-lg"
            >
              <Download className="w-4 h-4" />
              Installa App
            </button>
          )}
        </div>
      </header>

      {/* Modal Guida Installazione */}
      {showInstallGuide && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl">
            <div className="bg-[#FFE500] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/icons/logo-blumarlin.png" 
                  alt="Blu Marlin" 
                  className="w-12 h-12 object-contain rounded-lg"
                />
                <div>
                  <h3 className="font-bold text-[#0A1628]">Installa l'App</h3>
                  <p className="text-xs text-[#0A1628]/70">Blu Marlin Bivalvi</p>
                </div>
              </div>
              <button 
                onClick={() => setShowInstallGuide(false)}
                className="p-2 hover:bg-black/10 rounded-full"
              >
                <X className="w-5 h-5 text-[#0A1628]" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {isAndroid && deferredPrompt ? (
                <>
                  <p className="text-sm text-gray-600 text-center">
                    Installa l'app direttamente sul tuo telefono Android per un accesso rapido!
                  </p>
                  <button
                    onClick={handleInstallAndroid}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0A1628] text-[#FFE500] rounded-xl font-bold uppercase"
                  >
                    <Download className="w-5 h-5" />
                    Installa Ora
                  </button>
                </>
              ) : isIOS ? (
                <>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    Per installare l'app Operatore su iPhone/iPad:
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">0</span>
                      </div>
                      <div>
                        <p className="font-bold text-sm text-green-800">PRIMA DI TUTTO</p>
                        <p className="text-xs text-green-700">
                          Vai a questo indirizzo in Safari:<br/>
                          <code className="bg-green-100 px-1 rounded font-mono">/operatore.html</code>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0">
                        <Share className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">1. Tocca "Condividi"</p>
                        <p className="text-xs text-gray-500">L'icona con la freccia in basso nella barra di Safari</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#0A1628] rounded-full flex items-center justify-center flex-shrink-0">
                        <Plus className="w-4 h-4 text-[#FFE500]" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">2. "Aggiungi a Home"</p>
                        <p className="text-xs text-gray-500">Scorri e tocca "Aggiungi a schermata Home"</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#FFE500] rounded-full flex items-center justify-center flex-shrink-0">
                        <img src="/icons/icon-72x72.png" alt="" className="w-6 h-6 rounded" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">3. Conferma come "Operatore"</p>
                        <p className="text-xs text-gray-500">Cambia il nome in "Operatore" e tocca "Aggiungi"</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-center text-gray-400 mt-3">
                    L'app aprirà sempre e solo la sezione Operatore
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 text-center">
                    Per installare l'app, usa il menu del browser e seleziona "Installa app" o "Aggiungi a schermata Home"
                  </p>
                </>
              )}
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowInstallGuide(false)}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab selector */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        {/* Riga 1: Depurazione */}
        <p className="text-xs text-white/50 uppercase tracking-wider mb-2 ml-2">Depurazione</p>
        <div className="flex gap-2 p-1.5 bg-[#0A1628]/50 backdrop-blur rounded-xl border border-white/10 mb-4">
          <button
            onClick={() => setActiveTab('in')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold uppercase tracking-wide text-sm transition-all ${
              activeTab === 'in'
                ? 'bg-[#22C55E] text-white shadow-lg shadow-[#22C55E]/30'
                : 'text-white/70 hover:bg-white/10'
            }`}
            data-testid="tab-ingresso"
          >
            <LogIn className="w-5 h-5" />
            IN
          </button>
          <button
            onClick={() => setActiveTab('out')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold uppercase tracking-wide text-sm transition-all ${
              activeTab === 'out'
                ? 'bg-[#EF4444] text-white shadow-lg shadow-[#EF4444]/30'
                : 'text-white/70 hover:bg-white/10'
            }`}
            data-testid="tab-uscita"
          >
            <LogOut className="w-5 h-5" />
            OUT
          </button>
          <button
            onClick={() => setActiveTab('stato')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold uppercase tracking-wide text-sm transition-all ${
              activeTab === 'stato'
                ? 'bg-[#FFE500] text-[#0A1628] shadow-lg shadow-[#FFE500]/30'
                : 'text-white/70 hover:bg-white/10'
            }`}
            data-testid="tab-stato"
          >
            <Droplets className="w-5 h-5" />
            Docce
          </button>
        </div>

        {/* Riga 2: Cella */}
        <p className="text-xs text-white/50 uppercase tracking-wider mb-2 ml-2">Cella (No Depurazione)</p>
        <div className="flex gap-2 p-1.5 bg-[#8B5CF6]/10 backdrop-blur rounded-xl border border-[#8B5CF6]/30">
          <button
            onClick={() => setActiveTab('cella-in')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold uppercase tracking-wide text-sm transition-all ${
              activeTab === 'cella-in'
                ? 'bg-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/30'
                : 'text-white/70 hover:bg-white/10'
            }`}
            data-testid="tab-cella-in"
          >
            <Warehouse className="w-5 h-5" />
            Cella IN
          </button>
          <button
            onClick={() => setActiveTab('cella-out')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold uppercase tracking-wide text-sm transition-all ${
              activeTab === 'cella-out'
                ? 'bg-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/30'
                : 'text-white/70 hover:bg-white/10'
            }`}
            data-testid="tab-cella-out"
          >
            <Warehouse className="w-5 h-5" />
            Cella OUT
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="py-6 px-4">
        {activeTab === 'in' && <OperatoreIn />}
        {activeTab === 'out' && <OperatoreOut />}
        {activeTab === 'stato' && <OperatoreStatoDocce />}
        {activeTab === 'cella-in' && <OperatoreCellaIn />}
        {activeTab === 'cella-out' && <OperatoreCellaOut />}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <img 
          src="/icons/logo-blumarlin.png" 
          alt="Blu Marlin" 
          className="w-10 h-10 mx-auto mb-2 opacity-50"
        />
        <p className="text-xs text-white/30">
          Blu Marlin Bivalvi S.R.L.
        </p>
        <p className="text-xs text-white/20 mt-1">
          Sistema Depurazione Molluschi
        </p>
      </footer>
      
      <Toaster position="top-right" />
    </div>
  );
};

export default OperatorePortal;
